
import {ChangeDetectionStrategy, Component, Inject} from "@angular/core";
import {FormsModule} from "@angular/forms";
import {SafeHtml} from "@angular/platform-browser";

import {MaterialModule} from "framework/material";
import {Lookup} from "framework/services/utils.service";
import {ButtonComponent, ButtonStyle} from "framework/ui/buttons/button.component";
import {ChartColorUtilities} from "framework/ui/charting/core/colors";
import {BaseComponent} from "framework/ui/components";
import {OVERLAY_DATA, OverlayDialogRef} from "framework/ui/overlays/overlay-dialog";
import {OverlayComponent, OverlayConfig} from "framework/ui/overlays/overlay.component";

@Component({
    templateUrl: "./notification-popup.component.html",
    styleUrls: ["./notification-popup.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    ButtonComponent,
    MaterialModule,
    FormsModule
]
})
export class NotificationPopupComponent<T>
{
    private readonly m_color: string;
    public userInputMessage: string = "";

    get notificationColor(): string
    {
        return this.data.type === NotificationType.Error ? this.m_color : undefined;
    }

    get iconColor(): string
    {
        switch (this.data.type)
        {
            case NotificationType.Success:
            case NotificationType.Warning:
            case NotificationType.WarningWithAction:
            case NotificationType.Error:
                return this.m_color;

            default:
                return undefined;
        }
    }

    public isDisabled(action: NotificationAction<T>): boolean
    {
        return this.data.userInput && this.userInputMessage.toLowerCase() != "yes" && action.triggerAction;
    }

    constructor(public dialogRef: OverlayDialogRef<Promise<T>>,
                @Inject(OVERLAY_DATA) public data: DialogConfig<T>)
    {
        switch (this.data.type)
        {
            case NotificationType.Success:
                this.m_color = ChartColorUtilities.getColorById("Severity Colors", "low").hex;
                break;

            case NotificationType.Warning:
                this.m_color = ChartColorUtilities.getColorById("Severity Colors", "medium").hex;
                break;

            case NotificationType.WarningWithAction:
                this.m_color = ChartColorUtilities.getColorById("Severity Colors", "high").hex;
                break;

            case NotificationType.Error:
                this.m_color = ChartColorUtilities.getColorById("Severity Colors", "critical").hex;
                break;
        }
    }

    public static open<T>(comp: BaseComponent,
                          type: NotificationType,
                          notification: string,
                          icon: string,
                          additionalContext: string[],
                          actions?: NotificationAction<T>[],
                          userInput?: boolean,
                          showClose?: boolean,
                          width?: number,
                          actionSubText?: string,
                          requireExplicitAction?: boolean): Promise<T>
    {
        actions ||= [];
        userInput ||= false;
        showClose ||= false;
        width ||= 400;
        actionSubText ||= "";
        requireExplicitAction ||= false;
        return OverlayComponent.open(comp, NotificationPopupComponent, {
            data  : new DialogConfig(type, notification, icon, additionalContext, actions, userInput, showClose, width, actionSubText),
            config: OverlayConfig.dialog({
                                             showCloseButton    : showClose || !actions.length,
                                             width              : width,
                                             trapFocus          : false,
                                             closableViaBackdrop: !requireExplicitAction,
                                             keyboardCloseable  : !requireExplicitAction
                                         })
        });
    }
}

class DialogConfig<T>
{
    constructor(public readonly type: NotificationType,
                public readonly notification: string,
                public readonly icon: string,
                public readonly additionalContext: SafeHtml[],
                public readonly actions: NotificationAction<T>[],
                public readonly userInput: boolean,
                public readonly showClose: boolean,
                public readonly width: number,
                public readonly actionSubText: string)
    {}
}

export enum NotificationType
{
    Error,
    Warning,
    WarningWithAction,
    Success
}

export interface NotificationAction<T>
{
    label: string;
    priority: ButtonStyle;
    action?: () => Promise<T>;
    styles?: Lookup<string>;
    triggerAction?: boolean;
}
